/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {memo, useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Route, Switch, useRouteMatch, Redirect} from 'react-router'
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Stack,
    Text,
    HStack,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevDownIcon, ChevUpIcon, SignoutIcon} from '../../components/icons'
import AccountDashboard from './partials/dashboard'
import AccountAddresses from './partials/addresses/index'
import AccountWishlist from './partials/wishlist/index'
import {useLocation, Link as RouteLink} from 'react-router-dom'
import AccountOrders from './partials/orders'

import {messages, navLinks} from './constant'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {
    useAuthHelper,
    AuthHelpers,
    useShopperCustomersMutation,
    useUserInfo,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {pxToRem} from '../../utills/pxToRem'
import AccountRewards from './partials/rewards'
import RecommendedProducts from '../../components/recommended-products'
import {getUrlSearchParam} from '../../utills/url'
import AccountDetail from './partials/profile'
import {isProfileComplete} from '../../utills/helperServices'

const AccountBreadcrumbs = memo(({separator = '/', sx = {}}) => {
    const {formatMessage} = useIntl()
    const location = useLocation()
    const bcList = [{name: 'home', path: '/'}]
    const currentNav = navLinks.find(({path}) => location.pathname.includes(path))
    let currentTitle = currentNav?.name
    const dashboardCrumb = navLinks.find(({name}) => name === 'dashboard')
    if (!currentTitle) {
        currentTitle = dashboardCrumb.name
    }
    if (currentTitle !== 'dashboard') {
        bcList.push({...dashboardCrumb})
    }
    bcList.push({name: currentTitle})
    if (currentTitle === 'orders') {
        const pathArr = location.pathname?.split('/')
        const lastPathname = pathArr[pathArr.length - 1]
        if (!lastPathname.includes('orders')) {
            bcList.pop()
            bcList.push({...currentNav})
            bcList.push({name: 'orderDetails'})
        }
    }

    return (
        <HStack
            sx={sx}
            mb={{base: '20px', lg: '30px'}}
            px={{base: '11px', lg: '45px'}}
            color="cav_light_input_gray"
            fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
            fontStyle="normal"
            lineHeight="normal"
            letterSpacing="1px"
            fontFamily="Montserrat"
            spacing="1"
            flexWrap="wrap"
        >
            {bcList.map(({name, path}, i) => (
                <React.Fragment key={i}>
                    {path && (
                        <Link as={RouteLink} to={path} whiteSpace="nowrap">
                            {formatMessage(messages?.[name])}
                        </Link>
                    )}
                    {!path && (
                        <Text as="span" whiteSpace="nowrap">
                            {formatMessage(messages?.[name])}
                        </Text>
                    )}
                    {i < bcList.length - 1 && <Text as="span">{separator}</Text>}
                </React.Fragment>
            ))}
        </HStack>
    )
})

AccountBreadcrumbs.displayName = 'AccountBreadcrumbs'

AccountBreadcrumbs.propTypes = {
    separator: PropTypes.string,
    sx: PropTypes.object
}

const onClient = typeof window !== 'undefined'
const LogoutButton = memo(({onClick}) => {
    const intl = useIntl()
    const handleClick = useCallback(() => {
        onClick()
    }, [onClick])

    return (
        <Button
            as="li"
            fontWeight="semibold"
            onClick={handleClick}
            padding={4}
            py={0}
            variant="unstyled"
            _hover={{background: 'gray.50'}}
            marginTop={1}
            borderRadius="4px"
            cursor={'pointer'}
            mt="0"
        >
            <Flex justify="flex-start" align="center" height="100%">
                <SignoutIcon
                    boxSize={5}
                    mr={{base: pxToRem(15), md: pxToRem(10)}}
                    aria-hidden={true}
                />
                <Text
                    as="span"
                    fontWeight={{base: 'medium'}}
                    fontSize={['sm', 'md', 'md', 'md']}
                    lineHeight="normal"
                    letterSpacing={{base: pxToRem(1)}}
                >
                    {intl.formatMessage({
                        defaultMessage: 'Sign Out',
                        id: 'account.logout_button.button.sign_out'
                    })}
                </Text>
            </Flex>
        </Button>
    )
})

LogoutButton.displayName = 'LogoutButton'
LogoutButton.propTypes = {
    onClick: PropTypes.func.isRequired
}

const LAST_MODIFIER = 'INT_BOOMI'
const Account = () => {
    const {app: appConfig} = getConfig()
    const {path} = useRouteMatch()
    const {formatMessage} = useIntl()
    const {isExternal} = useCustomerType()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const [isUpdateCustomerLoading, setIsUpdateCustomerLoading] = useState(false)
    const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer()
    const isValidProfile = useMemo(() => {
        return isProfileComplete(customer)
    }, [customer])
    const {data: userInfo, isLoading: isUserInfoLoading} = useUserInfo({
        parameters: {
            channel_id: appConfig?.commerceAPI?.parameters?.siteId
        }
    })
    const {isRegistered, customerType} = customer
    const navigate = useNavigation()
    const isDesktop = useBreakpointValue({base: false, lg: true})

    const logout = useAuthHelper(AuthHelpers.Logout)
    const location = useLocation()

    const [mobileNavIndex, setMobileNavIndex] = useState(-1)
    const [showLoading, setShowLoading] = useState(false)

    const einstein = useEinstein()

    const {buildUrl} = useMultiSite()
    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(location.pathname)
    }, [location])

    const onSignoutClick = useCallback(async () => {
        setShowLoading(true)
        await logout.mutateAsync()
    }, [])

    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [location.search])

    const updateUserInfo = useCallback(async () => {
        if (!userInfo) {
            return
        }
        setIsUpdateCustomerLoading(true)
        await updateCustomerMutation.mutateAsync(
            {
                parameters: {customerId: customer?.customerId},
                body: {
                    firstName: userInfo?.given_name,
                    lastName: userInfo?.family_name
                }
            },
            {
                onSuccess: () => {
                    const profilePath = buildUrl('/account/info')
                    navigate(profilePath)
                }
            }
        )
        setIsUpdateCustomerLoading(false)
    }, [userInfo?.given_name, userInfo?.family_name])

    useEffect(() => {
        if (isRegistered && onClient && location.pathname !== '/account/info') {
            if (customer?.c_lastModifiedBy === LAST_MODIFIER && !isValidProfile) {
                if (isExternal) {
                    updateUserInfo()
                } else {
                    const profilePath = buildUrl('/account/info')
                    navigate(profilePath)
                }
            }
        }
    }, [isValidProfile, isRegistered, onClient, location])

    // If we have customer data and they are not registered, push to login page
    // Using Redirect allows us to store the directed page to location
    // so we can direct users back after they are successfully log in
    if (customerType !== null && !isRegistered && onClient) {
        const path = buildUrl('/login')
        return <Redirect to={{pathname: path, state: {directedFrom: '/account'}}} />
    }
    if (isCustomerLoading || (isExternal && (isUserInfoLoading || isUpdateCustomerLoading))) {
        return <LoadingSpinner wrapperStyles={{height: '100vh'}} />
    }

    return (
        <Box
            data-testid={isRegistered && isHydrated() ? 'account-page' : 'account-page-skeleton'}
            layerStyle="page"
            paddingTop={[4, 5]}
        >
            <AccountBreadcrumbs />
            <Seo title="My Account" description="Customer Account Page" />
            <Grid
                templateColumns={{base: '1fr', lg: '340px calc(100% - 340px - 30px)'}} // 30px gap and 340px left nav width
                gap={{base: pxToRem(10), lg: pxToRem(30)}}
            >
                {/* small screen nav accordion */}
                <>
                    <Heading
                        display={{base: 'initial', lg: 'none'}}
                        px={pxToRem(11)}
                        as="h2"
                        mb="0"
                        fontFamily="Montserrat"
                        fontSize={pxToRem(22)}
                        fontWeight="semibold"
                        letterSpacing={pxToRem(3)}
                        lineHeight="normal"
                    >
                        <FormattedMessage
                            defaultMessage="Hello, {name}!"
                            id="account.heading.greetings"
                            values={{name: customer?.firstName}}
                        />
                    </Heading>
                    <Accordion
                        display={{base: 'initial', lg: 'none'}}
                        allowToggle={true}
                        reduceMotion={true}
                        index={mobileNavIndex}
                        onChange={setMobileNavIndex}
                        px="11px"
                    >
                        <AccordionItem border="none" background="gray.50" borderRadius="base">
                            {({isExpanded}) => (
                                <>
                                    <AccordionButton
                                        as={Button}
                                        height={10}
                                        border="1px solid black"
                                        borderRadius="none"
                                        padding="12px 17px 13px 16px"
                                        variant="ghost"
                                        color="black"
                                        _active={{background: 'gray.100'}}
                                        _expanded={{background: 'transparent'}}
                                    >
                                        <Flex align="center" justify="space-between" width="full">
                                            <Heading
                                                as="h2"
                                                fontSize="xs"
                                                fontWeight="medium"
                                                lineHeight="normal"
                                                fontFamily="Montserrat"
                                                letterSpacing={pxToRem(1)}
                                                mb="0"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="My Account"
                                                    id="account.accordion.button.my_account"
                                                />
                                            </Heading>
                                            {isExpanded ? (
                                                <ChevUpIcon boxSize={pxToRem(17)} />
                                            ) : (
                                                <ChevDownIcon boxSize={pxToRem(17)} />
                                            )}
                                        </Flex>
                                    </AccordionButton>
                                    <AccordionPanel
                                        py="10px"
                                        px="0"
                                        border="1px solid black"
                                        borderTop="none"
                                    >
                                        <Flex
                                            id={
                                                !isDesktop
                                                    ? 'account-detail-nav'
                                                    : 'hidden-account-detail-nav'
                                            }
                                            as="nav"
                                            spacing={0}
                                            direction="column"
                                        >
                                            <Stack
                                                pl="0"
                                                mb="0"
                                                spacing={0}
                                                as="ul"
                                                data-testid="account-nav"
                                                sx={{
                                                    '.active': {
                                                        borderWidth: '0 0 0 5px',
                                                        borderRadius: 'base'
                                                    }
                                                }}
                                            >
                                                {navLinks.map((link) => (
                                                    <Box
                                                        align="center"
                                                        key={link.name}
                                                        as="li"
                                                        listStyleType="none"
                                                        height={10}
                                                    >
                                                        <Button
                                                            as={Link}
                                                            to={link.path}
                                                            useNavLink={true}
                                                            variant="menu-link-mobile"
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            letterSpacing="1px"
                                                            lineHeight="normal"
                                                            fontStyle="normal"
                                                            width="100%"
                                                            borderLeftColor="cav_red"
                                                            sx={{
                                                                span: {
                                                                    mr: pxToRem(15)
                                                                }
                                                            }}
                                                            leftIcon={
                                                                <link.icon
                                                                    boxSize={5}
                                                                    width={
                                                                        !link.hideName
                                                                            ? 'auto'
                                                                            : '115px'
                                                                    }
                                                                    color={
                                                                        !link.hideName
                                                                            ? ''
                                                                            : 'cav_red'
                                                                    }
                                                                    ml={
                                                                        !link.hideName
                                                                            ? '0'
                                                                            : '-4px'
                                                                    }
                                                                />
                                                            }
                                                            onClick={() => setMobileNavIndex(-1)}
                                                        >
                                                            {!link.hideName
                                                                ? formatMessage(messages[link.name])
                                                                : ''}
                                                        </Button>
                                                    </Box>
                                                ))}

                                                <LogoutButton onClick={onSignoutClick} />
                                            </Stack>
                                        </Flex>
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    </Accordion>
                </>

                {/* large screen nav sidebar */}
                <Stack
                    display={{base: 'none', lg: 'flex'}}
                    paddingLeft={{md: pxToRem(45)}}
                    spacing={4}
                >
                    {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}

                    <Heading
                        as="h2"
                        fontFamily="Montserrat"
                        fontSize={pxToRem(30)}
                        fontWeight="semibold"
                        letterSpacing={pxToRem(4)}
                        mb={pxToRem(9)}
                    >
                        <FormattedMessage
                            defaultMessage="Hello, {name}!"
                            id="account.heading.greetings"
                            values={{name: customer?.firstName}}
                        />
                    </Heading>

                    <Flex
                        id={isDesktop ? 'account-detail-nav' : 'hidden-account-detail-nav'}
                        spacing={0}
                        as="nav"
                        data-testid="account-detail-nav"
                        direction="column"
                    >
                        {navLinks.map((link) => {
                            const LinkIcon = link.icon
                            return (
                                <Button
                                    key={link.name}
                                    as={Link}
                                    to={link.path}
                                    useNavLink={true}
                                    variant="menu-link"
                                    sx={{
                                        span: {
                                            mr: pxToRem(10)
                                        }
                                    }}
                                    fontSize="md"
                                    lineHeight="normal"
                                    fontWeight="medium"
                                    letterSpacing={pxToRem(1)}
                                    leftIcon={
                                        <LinkIcon
                                            boxSize={5}
                                            width={!link.hideName ? 'auto' : '115px'}
                                            color={!link.hideName ? '' : 'cav_red'}
                                            ml={!link.hideName ? '0' : '-4px'}
                                        />
                                    }
                                    borderLeftColor="cav_red !important"
                                >
                                    {!link.hideName ? formatMessage(messages[link.name]) : ''}
                                </Button>
                            )
                        })}
                        <LogoutButton onClick={onSignoutClick} />
                    </Flex>
                </Stack>

                <Switch>
                    <Route exact path={`${path}/dashboard`}>
                        <AccountDashboard />
                    </Route>
                    <Route path={`${path}/orders`}>
                        <AccountOrders />
                    </Route>
                    <Route exact path={`${path}/addresses`}>
                        <AccountAddresses />
                    </Route>
                    <Route exact path={`${path}/info`}>
                        <AccountDetail />
                    </Route>
                    <Route exact path={`${path}/rewards`}>
                        <AccountRewards />
                    </Route>
                    <Route exact path={`${path}/wishlist`}>
                        <AccountWishlist />
                    </Route>
                    <Route path={path}>
                        <Redirect to={`${path}/dashboard`} />
                    </Route>
                </Switch>
            </Grid>
            {location.pathname === navLinks.find(({name}) => name === 'myCavenders')?.path ? (
                <Box
                    mt="0 !important"
                    ml={{base: '11px', lg: '45px'}}
                    mr={{base: '11px', lg: '0'}}
                    sx={{
                        '> div': {
                            '> div': {
                                '> div:last-child': {
                                    padding: {base: '0', lg: '0 45px 0 0'}
                                }
                            }
                        }
                    }}
                >
                    <RecommendedProducts
                        title={formatMessage({
                            id: 'my_cavenders.my_rewards.recommendation.recommended_for_you',
                            defaultMessage: 'Recommended for you'
                        })}
                        recommender={'best-sellers-null-results'}
                        previousSearchParams={previousSearchParams}
                    />
                </Box>
            ) : null}
        </Box>
    )
}

Account.getTemplateName = () => 'account'

Account.propTypes = {
    match: PropTypes.object
}

export default Account
